// client/src/components/vatCredit/VatCreditCostBreakdown.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatCostAmount } from './vatCreditUtils';

/**
 * Riepilogo del costo per l'azienda, una riga per offerta allocata:
 * importo allocato, tasso, giorni, interessi, commissione, spese e costo totale.
 *
 * rows: [{ key, bankName, amount, cost, notAllocated }] dove `cost` è il risultato di computeBidCost
 *       (o il costo salvato all'allocazione), null se non calcolabile (offerte legacy).
 *       `notAllocated`: offerta ancora senza importo in fase di allocazione (riga senza costi).
 * baseRateDate: data dell'Euribor usato per le offerte variabili, se presenti (solo stima live).
 * amountLabel: intestazione della colonna importo (default "Importo allocato").
 * showTotals: riga dei totali, da nascondere quando le righe non sono importi allocati (calcolatore).
 */
const VatCreditCostBreakdown = ({ rows, currency, baseRateDate, amountLabel, showTotals = true }) => {
  const { t } = useTranslation();
  const money = (v) => formatCostAmount(v, currency);
  const formatRate = (v) => `${v.toFixed(3)}%`;

  if (rows.length === 0) {
    return <div className="vc-cost-empty">{t('vatCredit.cost.empty')}</div>;
  }

  const totals = rows.reduce((acc, { amount, cost }) => ({
    amount: acc.amount + amount,
    interestAmount: acc.interestAmount + (cost?.interestAmount || 0),
    commissionAmount: acc.commissionAmount + (cost?.commissionAmount || 0),
    fees: acc.fees + (cost?.fees || 0),
    totalCost: acc.totalCost + (cost?.totalCost || 0)
  }), { amount: 0, interestAmount: 0, commissionAmount: 0, fees: 0, totalCost: 0 });

  const hasMissingCost = rows.some(r => !r.notAllocated && !r.cost);

  return (
    <div className="vc-cost-breakdown">
      <div className="vc-cost-table-wrapper">
        <table className="vc-cost-table">
          <colgroup>
            <col className="vc-col-bank" />
            <col className="vc-col-amount" />
            <col className="vc-col-rate" />
            <col className="vc-col-days" />
            <col className="vc-col-interest" />
            <col className="vc-col-commission" />
            <col className="vc-col-fees" />
            <col className="vc-col-total" />
          </colgroup>
          <thead>
            <tr>
              <th>{t('bids.bank')}</th>
              <th>{amountLabel || t('vatCredit.cost.allocatedAmount')}</th>
              <th>{t('vatCredit.cost.rate')}</th>
              <th className="vc-cost-days">{t('vatCredit.cost.days')}</th>
              <th>{t('vatCredit.cost.interest')}</th>
              <th>{t('vatCredit.cost.commission')}</th>
              <th>{t('vatCredit.fees')}</th>
              <th>{t('vatCredit.cost.totalCost')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ key, bankName, amount, cost, notAllocated }) => (
              <tr key={key}>
                <td className="vc-cost-bank">{bankName}</td>
                <td>{money(amount)}</td>
                {notAllocated ? (
                  <td colSpan="6" className="vc-cost-na">{t('vatCredit.cost.notAllocated')}</td>
                ) : cost ? (
                  <>
                    <td>{formatRate(cost.effectiveRate)}</td>
                    <td className="vc-cost-days">{cost.days}</td>
                    <td>{money(cost.interestAmount)}</td>
                    <td>{money(cost.commissionAmount)}</td>
                    <td>{money(cost.fees)}</td>
                    <td className="vc-cost-total">{money(cost.totalCost)}</td>
                  </>
                ) : (
                  <td colSpan="6" className="vc-cost-na">{t('vatCredit.cost.notAvailable')}</td>
                )}
              </tr>
            ))}
          </tbody>
          {showTotals && (
            <tfoot>
              <tr>
                <td>{t('vatCredit.cost.total')}</td>
                <td>{money(totals.amount)}</td>
                <td />
                <td className="vc-cost-days" />
                <td>{money(totals.interestAmount)}</td>
                <td>{money(totals.commissionAmount)}</td>
                <td>{money(totals.fees)}</td>
                <td className="vc-cost-total">{money(totals.totalCost)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="vc-cost-notes">
        <div>{t('vatCredit.cost.formula')}</div>
        {baseRateDate && (
          <div>{t('vatCredit.cost.floatingEstimate', { date: new Date(baseRateDate).toLocaleDateString() })}</div>
        )}
        {hasMissingCost && <div>{t('vatCredit.cost.legacyNote')}</div>}
      </div>
    </div>
  );
};

export default VatCreditCostBreakdown;
